export const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

type TestConfigProperty = {
  id: string;
  name: string;
  baseUrl: string;
  environments: Array<{
    id: string;
    name: string;
    url: string;
    isDefault: boolean;
    environmentKindId?: string | null;
    environmentKindName?: string | null;
  }>;
};

type TestConfigResponse = {
  ok: boolean;
  properties: TestConfigProperty[];
  error?: string;
};

export type ResolvedConfig = {
  propertyId?: string;
  environmentId?: string;
};

export async function resolveTestConfig(
  apiUrl: string,
  token: string,
  propertyArg?: string,
  environmentArg?: string
): Promise<ResolvedConfig> {
  if (!propertyArg && !environmentArg) return {};

  const res = await fetch(`${apiUrl}/workflows/test-config`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Failed to fetch test config: ${res.status} ${text}`);
  }

  const data = (await res.json()) as TestConfigResponse;
  if (!data.ok || !data.properties) {
    throw new Error(`Failed to fetch test config: ${data.error ?? 'Unknown error'}`);
  }

  const { properties } = data;
  let property: TestConfigProperty | undefined;

  if (propertyArg) {
    if (UUID_RE.test(propertyArg)) {
      property = properties.find((p) => p.id === propertyArg);
    } else {
      property = properties.find((p) => p.name.toLowerCase() === propertyArg.toLowerCase());
    }
    if (!property) {
      const available = properties.map((p) => `  - ${p.name} (${p.id})`).join('\n');
      throw new Error(
        `Application "${propertyArg}" not found.\nAvailable applications:\n${available}`
      );
    }
  } else if (environmentArg) {
    // --environment without --application: auto-resolve if single application
    if (properties.length === 0) {
      throw new Error('No applications found in your organization.');
    }
    if (properties.length > 1) {
      const available = properties.map((p) => `  - ${p.name} (${p.id})`).join('\n');
      throw new Error(
        `--environment requires --application when multiple applications exist.\nAvailable applications:\n${available}`
      );
    }
    property = properties[0];
  }

  const result: ResolvedConfig = {};
  if (property) {
    result.propertyId = property.id;

    if (environmentArg) {
      let env: TestConfigProperty['environments'][number] | undefined;
      if (UUID_RE.test(environmentArg)) {
        env = property.environments.find((e) => e.id === environmentArg);
      } else {
        env = property.environments.find(
          (e) =>
            e.name.toLowerCase() === environmentArg.toLowerCase() ||
            e.environmentKindName?.toLowerCase() === environmentArg.toLowerCase()
        );
      }
      if (!env) {
        const available = property.environments
          .map((e) => `  - ${e.name}${e.isDefault ? ' (default)' : ''} (${e.id})`)
          .join('\n');
        throw new Error(
          `Environment "${environmentArg}" not found for application "${property.name}".\nAvailable environments:\n${available}`
        );
      }
      result.environmentId = env.id;
    }
  }

  return result;
}
